class DlOfferBar {
  constructor(offerBar, dataEl) {
    this.offerBar = offerBar;
    this.ctx = {
      variantWeight:    parseInt(dataEl.dataset.weight || '0', 10),
      gramsPerCup:      parseFloat(dataEl.dataset.gramsPerCup || '0'),
      compareAtPrice:   parseInt(dataEl.dataset.compareAtPrice || '0', 10),
      variantPrice:     parseInt(dataEl.dataset.variantPrice || '0', 10),
      currency:         dataEl.dataset.currency || '€',
      layout:           dataEl.dataset.layout || 'row',
      inventory:        parseInt(dataEl.dataset.inventory || '-1', 10),
      inventoryPolicy:  dataEl.dataset.inventoryPolicy || 'deny',
      textSave:         dataEl.dataset.textSave || 'Save',
      textPerCup:       dataEl.dataset.textPerCup || '/ cup',
      textNotAvailable: dataEl.dataset.textNotAvailable || 'Not available',
    };
    /* Free-shipping badge: threshold (presentment-currency cents, 0 = disabled) + localized text.
       Provided by snippets/dl-offer-free-shipping.liquid (rendered via theme-scripts.liquid). */
    const fsEl = document.getElementById('dl-offer-free-shipping-data') || dataEl;
    let fsThreshold = parseInt(fsEl.dataset.freeShippingThreshold || '0', 10);
    const fsCurrency = fsEl.dataset.freeShippingCurrency || '';
    const shopCur = (window.Shopify && window.Shopify.currency) || {};
    /* Rule currency differs from the displayed currency (e.g. CHF storefront, EUR rule): convert like checkout does. */
    if (fsThreshold && fsCurrency && shopCur.active && shopCur.active !== fsCurrency) {
      const rate = parseFloat(shopCur.rate || '1');
      fsThreshold = rate > 0 ? Math.round(fsThreshold * rate) : 0;
    }
    this.ctx.freeShipThreshold = fsThreshold;
    this.ctx.textFreeShip      = fsEl.dataset.textFreeShipping || 'Free shipping';
    this.ctx.textShipRemaining         = fsEl.dataset.textShippingRemaining || '';
    this.ctx.textShipRemainingTier     = fsEl.dataset.textShippingRemainingTier || '';
    this.ctx.textShipRemainingSave     = fsEl.dataset.textShippingRemainingSave || '';
    this.ctx.textShipRemainingTierSave = fsEl.dataset.textShippingRemainingTierSave || '';
    this.ctx.textShipIncluded          = fsEl.dataset.textShippingIncluded || '';
    /* Standard shipping cost (rule currency -> displayed currency, same conversion as the threshold). */
    let shipCost = parseInt(fsEl.dataset.shippingCost || '0', 10);
    if (shipCost && fsCurrency && shopCur.active && shopCur.active !== fsCurrency) {
      const rate = parseFloat(shopCur.rate || '1');
      shipCost = rate > 0 ? Math.round(shipCost * rate) : 0;
    }
    this.ctx.shipCost = shipCost;
    this.ctx.cartUrl  = fsEl.dataset.cartUrl || '/cart.js';
    this.cartTotal = 0;           // current cart subtotal (after discounts) in displayed-currency cents
    this.cartRefreshTimer = null;
    this.bannerScheduled = false;
    this.maintenanceScheduled = false;
  }

  init() {
    if (this.isReady()) {
      this.applyRedesign();
      return;
    }

    const observer = new MutationObserver(() => {
      if (this.isReady()) {
        this.applyRedesign();
        observer.disconnect();
      }
    });
    observer.observe(this.offerBar, { attributes: true, childList: true, subtree: true });
  }

  isReady() {
    return this.offerBar.style.display !== 'none'
      && this.offerBar.querySelectorAll('.dl-offer-tier-row').length > 0;
  }

  applyRedesign() {
    document.body.classList.add('has-offer-bar');

    if (this.ctx.layout === 'column') {
      this.offerBar.classList.add('dl-offer-bar--vertical');
    }

    this.processTiers();
    this.bindUserPickFlag();
    this.preselectFirst();
    this.hideSavingsIfAllSoldOut();
    this.setupMaintenanceObserver();
    this.scheduleBannerUpdate();
    this.setupCartSync();
  }

  /* ---- Cart sync: the banner counts what is already in the cart ----
     Reads /cart.js once and again after any cart mutation (add / change / update / clear) made via fetch or XHR.
     Failures are silent: the banner then simply counts the selected option alone. */
  setupCartSync() {
    this.refreshCart();
    if (DlOfferBar.cartHooked) {
      DlOfferBar.instances.push(this);
      return;
    }
    DlOfferBar.cartHooked = true;
    DlOfferBar.instances = [this];

    const isCartMutation = (url) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
    const notify = () => DlOfferBar.instances.forEach((i) => i.queueCartRefresh());

    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : (input && input.url);
        const p = origFetch.apply(this, arguments);
        if (isCartMutation(url)) p.then(notify, () => {});
        return p;
      };
    }
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartMutation(url)) this.addEventListener('loadend', notify);
      return origOpen.apply(this, arguments);
    };
    /* Common theme/app events after cart changes */
    ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added', 'product:added'].forEach((ev) => {
      document.addEventListener(ev, notify);
    });
  }

  queueCartRefresh() {
    clearTimeout(this.cartRefreshTimer);
    this.cartRefreshTimer = setTimeout(() => this.refreshCart(), 400);
  }

  refreshCart() {
    if (!this.ctx.freeShipThreshold) return;
    if (typeof window.fetch !== 'function') return;
    fetch(this.ctx.cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        const shopCur = (window.Shopify && window.Shopify.currency) || {};
        /* Only trust totals in the displayed currency */
        if (cart.currency && shopCur.active && cart.currency !== shopCur.active) return;
        const total = parseInt(cart.total_price, 10);
        if (isNaN(total)) return;
        if (total !== this.cartTotal) {
          this.cartTotal = total;
          this.scheduleBannerUpdate();
        }
      })
      .catch(() => {});
  }

  /* Mark the tier list once the user genuinely picks a tier, so CSS stops forcing the first-selected
     look. isTrusted filters out our own synthetic preselectFirst().click(). */
  bindUserPickFlag() {
    this.offerBar.addEventListener('click', (e) => {
      if (!e.isTrusted) return;
      if (!e.target.closest('.dl-offer-tier-row')) return;
      const list = this.offerBar.querySelector('.dl-offer-tier-rows');
      if (list) list.classList.add('dl-offer-user-picked');
      this.scheduleBannerUpdate();
    });
  }

  processTiers() {
    this.offerBar.querySelectorAll('.dl-offer-tier-row').forEach((tier) => {
      const qty = parseInt(tier.dataset.qty || '1', 10);
      const soldOut = this.isSoldOut(qty);

      this.injectWeight(tier, qty);
      this.updateOrigPrice(tier, qty);
      this.fixBundlePrice(tier, qty);

      if (soldOut) {
        this.disableTier(tier);
        this.injectSaveBadge(tier, qty);
        return;
      }

      this.injectCupPrice(tier, qty);
      this.injectSaveBadge(tier, qty);
      this.injectFreeShippingBadge(tier);
    });
  }

  /* Init only: always preselect the first available tier (client-requested), even if the app defaulted to another. */
  preselectFirst() {
    const firstAvailable = this.offerBar.querySelector('.dl-offer-tier-row:not(.dl-offer-tier-row--soldout)');
    if (firstAvailable && !firstAvailable.classList.contains('dl-offer-tier-row--active')) {
      firstAvailable.click();
    }
  }

  /* Maintenance path: only re-select when the active tier became invalid — must not hijack the user's choice. */
  selectFirstAvailable() {
    const activeTier = this.offerBar.querySelector('.dl-offer-tier-row--active');
    const firstAvailable = this.offerBar.querySelector('.dl-offer-tier-row:not(.dl-offer-tier-row--soldout)');
    if (!activeTier || activeTier.classList.contains('dl-offer-tier-row--soldout')) {
      if (firstAvailable) firstAvailable.click();
    }
  }

  hideSavingsIfAllSoldOut() {
    const firstAvailable = this.offerBar.querySelector('.dl-offer-tier-row:not(.dl-offer-tier-row--soldout)');
    if (!firstAvailable) {
      const savings = this.offerBar.querySelector('.dl-offer-savings');
      if (savings) savings.style.setProperty('display', 'none', 'important');
    }
  }

  /* Maintains sold-out state in case the app re-renders/re-activates tiers */
  setupMaintenanceObserver() {
    const mo = new MutationObserver(() => this.maintain());
    mo.observe(this.offerBar, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  maintain() {
    this.scheduleBannerUpdate();
    if (this.maintenanceScheduled) return;
    this.maintenanceScheduled = true;
    requestAnimationFrame(() => {
      this.maintenanceScheduled = false;
      let changed = false;
      this.offerBar.querySelectorAll('.dl-offer-tier-row').forEach((tier) => {
        const qty = parseInt(tier.dataset.qty || '1', 10);
        if (this.isSoldOut(qty) && !tier.classList.contains('dl-offer-tier-row--soldout')) {
          this.disableTier(tier);
          this.injectSaveBadge(tier, qty);
          changed = true;
        }
      });
      if (changed) this.selectFirstAvailable();
    });
  }

  injectWeight(tier, qty) {
    if (tier.querySelector('.dl-offer-weight')) return;
    if (!this.ctx.variantWeight) return;

    const totalG = this.ctx.variantWeight * qty;
    const el = document.createElement('span');
    el.className = 'dl-offer-weight';
    el.textContent = DlOfferBar.formatGrams(totalG);
    tier.insertBefore(el, tier.firstChild);
  }

  /* Update / create the original (compare) price = compareAtPrice × qty */
  updateOrigPrice(tier, qty) {
    if (!this.ctx.compareAtPrice) return;

    const compareTotal = this.ctx.compareAtPrice * qty;
    const formatted = DlOfferBar.formatMoney(compareTotal, this.ctx.currency);
    let origEl = tier.querySelector('.dl-offer-tier-row__orig');

    if (origEl) {
      origEl.textContent = formatted;
    } else {
      // Tier 1 (qty=1) has no orig element — create one
      const priceEl = tier.querySelector('.dl-offer-tier-row__price');
      if (!priceEl) return;
      origEl = document.createElement('span');
      origEl.className = 'dl-offer-tier-row__orig';
      origEl.textContent = formatted;
      priceEl.after(origEl);
    }
  }

  /* Fix bundle total price: unit_price × qty (matches Shopify cart rounding) */
  fixBundlePrice(tier, qty) {
    const unitPriceCents = this.parseUnitPrice(tier, 0);
    if (!unitPriceCents || qty === 1) return;

    const correctTotal = unitPriceCents * qty;
    const priceEl = tier.querySelector('.dl-offer-tier-row__price');
    if (priceEl) {
      priceEl.textContent = DlOfferBar.formatMoney(correctTotal, this.ctx.currency);
    }
  }

  injectCupPrice(tier, qty) {
    if (tier.querySelector('.dl-offer-cup-price')) return;
    if (!this.ctx.gramsPerCup || !this.ctx.variantWeight) return;

    const unitPriceCents = this.parseUnitPrice(tier, this.ctx.variantPrice);
    if (!unitPriceCents) return;

    const cupPriceCents = Math.round(unitPriceCents * this.ctx.gramsPerCup / this.ctx.variantWeight);
    const el = document.createElement('span');
    el.className = 'dl-offer-cup-price';
    el.textContent = DlOfferBar.formatMoney(cupPriceCents, this.ctx.currency) + ' ' + this.ctx.textPerCup;

    const priceCol = tier.querySelector('.dl-offer-tier-row__price-col');
    if (priceCol) {
      priceCol.after(el);
    } else {
      tier.appendChild(el);
    }
  }

  /* Save badge = compareAtPrice × qty − bundlePrice, or orig − sale if no compare price */
  injectSaveBadge(tier, qty) {
    if (tier.querySelector('.dl-offer-save-badge')) return;

    const bundlePriceCents = this.parseBundlePrice(tier);
    if (!bundlePriceCents) return;

    let savingsCents = 0;

    if (this.ctx.compareAtPrice) {
      savingsCents = this.ctx.compareAtPrice * qty - bundlePriceCents;
    } else {
      const origEl = tier.querySelector('.dl-offer-tier-row__orig');
      if (origEl) {
        const origCents = DlOfferBar.parsePriceCents(origEl.textContent, this.ctx.currency);
        savingsCents = origCents - bundlePriceCents;
      }
    }

    if (savingsCents <= 0) return;

    const el = document.createElement('span');
    el.className = 'dl-offer-save-badge';
    el.textContent = this.ctx.textSave + ' ' + DlOfferBar.formatMoney(savingsCents, this.ctx.currency);
    tier.appendChild(el);
  }

  /* Free-shipping badge: shown when the tier's total price reaches the market's free-shipping threshold */
  injectFreeShippingBadge(tier) {
    if (!this.ctx.freeShipThreshold) return;
    if (tier.querySelector('.dl-offer-free-shipping')) return;

    const bundlePriceCents = this.parseBundlePrice(tier);
    if (!bundlePriceCents || bundlePriceCents < this.ctx.freeShipThreshold) return;

    const el = document.createElement('span');
    el.className = 'dl-offer-free-shipping';
    el.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 3h13v13H1z"/><path d="M14 8h5l4 4v4h-9z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
    el.appendChild(document.createTextNode(this.ctx.textFreeShip));
    tier.appendChild(el);
  }

  /* ---- Dynamic free-shipping banner (block 'free_shipping_banner' in main-product) ----
     Reflects the selected tier: "still X to go" (with the first qualifying tier as hint) or "included".
     Leaves the static block text untouched when the badge is disabled or the banner is missing. */
  scheduleBannerUpdate() {
    if (this.bannerScheduled) return;
    this.bannerScheduled = true;
    requestAnimationFrame(() => {
      this.bannerScheduled = false;
      this.updateShippingBanner();
    });
  }

  updateShippingBanner() {
    if (!this.ctx.freeShipThreshold) return;
    if (!this.ctx.textShipRemaining || !this.ctx.textShipIncluded) return;

    const textEl = document.querySelector('.product-free-shipping-banner__text');
    if (!textEl) return;

    const tiers = [...this.offerBar.querySelectorAll('.dl-offer-tier-row:not(.dl-offer-tier-row--soldout)')];
    if (!tiers.length) return;

    const list = this.offerBar.querySelector('.dl-offer-tier-rows');
    const userPicked = list && list.classList.contains('dl-offer-user-picked');
    /* Before a real user pick the CSS presents the first tier as selected — mirror that here. */
    let active = userPicked ? tiers.find((t) => t.classList.contains('dl-offer-tier-row--active')) : null;
    if (!active) active = tiers[0];

    const price = this.parseBundlePrice(active);
    if (!price) return;

    /* Cart-aware: what is already in the cart counts towards the threshold */
    const base = this.cartTotal || 0;
    const remaining = this.ctx.freeShipThreshold - base - price;
    let text;
    if (remaining <= 0) {
      text = this.ctx.textShipIncluded;
    } else {
      const amount = DlOfferBar.formatMoneyInSentence(remaining, this.ctx.currency);
      const shipping = this.ctx.shipCost ? DlOfferBar.formatMoneyInSentence(this.ctx.shipCost, this.ctx.currency) : '';
      /* Cheapest option that (together with the cart) unlocks free shipping */
      const qualifying = tiers.find((t) => t !== active && base + this.parseBundlePrice(t) >= this.ctx.freeShipThreshold);
      let tierLabel = qualifying ? ((qualifying.querySelector('.dl-offer-weight') || {}).textContent || '') : '';
      tierLabel = tierLabel.trim().replace(/(\d)(k?g|к?г)$/, '$1 $2');

      if (qualifying && tierLabel && shipping && this.ctx.textShipRemainingTierSave) {
        text = this.ctx.textShipRemainingTierSave.replace('[amount]', amount).replace('[tier]', tierLabel).replace('[shipping]', shipping);
      } else if (qualifying && tierLabel && this.ctx.textShipRemainingTier) {
        text = this.ctx.textShipRemainingTier.replace('[amount]', amount).replace('[tier]', tierLabel);
      } else if (shipping && this.ctx.textShipRemainingSave) {
        text = this.ctx.textShipRemainingSave.replace('[amount]', amount).replace('[shipping]', shipping);
      } else {
        text = this.ctx.textShipRemaining.replace('[amount]', amount);
      }
    }

    if (textEl.textContent !== text) {
      textEl.textContent = text;
      textEl.closest('.product-free-shipping-banner')?.classList.add('product-free-shipping-banner--dynamic');
    }
  }

  parseBundlePrice(tier) {
    const priceEl = tier.querySelector('.dl-offer-tier-row__price');
    if (!priceEl) return 0;
    return DlOfferBar.parsePriceCents(priceEl.textContent, this.ctx.currency);
  }

  parseUnitPrice(tier, fallback) {
    const desc = tier.querySelector('.dl-offer-tier-row__desc');
    if (!desc) return fallback;
    const cents = DlOfferBar.parsePriceCents(desc.textContent, this.ctx.currency);
    return cents || fallback;
  }

  isSoldOut(qty) {
    if (this.ctx.inventoryPolicy === 'continue') return false;
    if (this.ctx.inventory < 0) return false;
    return qty > this.ctx.inventory;
  }

  disableTier(tier) {
    tier.classList.add('dl-offer-tier-row--soldout');
    tier.classList.remove('dl-offer-tier-row--active');
    tier.style.pointerEvents = 'none';

    const freeShip = tier.querySelector('.dl-offer-free-shipping');
    if (freeShip) freeShip.remove();

    if (!tier.querySelector('.dl-offer-soldout-label')) {
      const label = document.createElement('span');
      label.className = 'dl-offer-soldout-label';
      label.textContent = this.ctx.textNotAvailable;
      tier.appendChild(label);
    }
  }

  static parsePriceCents(text, currency) {
    const stripped = text.replace(currency, '');
    const match = stripped.match(/[\d.,'’  ]+/);
    if (!match) return 0;

    const s = match[0].replace(/['’  ]/g, '');
    if (!s) return 0;

    const lastComma = s.lastIndexOf(',');
    const lastDot   = s.lastIndexOf('.');
    const decIdx    = Math.max(lastComma, lastDot);

    let num;
    if (decIdx === -1) {
      num = parseFloat(s);
    } else {
      const afterSep = s.length - decIdx - 1;
      if (afterSep === 1 || afterSep === 2) {
        const intPart = s.slice(0, decIdx).replace(/[.,]/g, '');
        const decPart = s.slice(decIdx + 1);
        num = parseFloat(intPart + '.' + decPart);
      } else {
        num = parseFloat(s.replace(/[.,]/g, ''));
      }
    }

    return isNaN(num) ? 0 : Math.round(num * 100);
  }

  static formatMoney(cents, currency) {
    const sym = currency || '€';
    return sym + (cents / 100).toFixed(2).replace('.', ',');
  }

  /* Running text: "20,10 €" for de/ru, "€20.10" for en (cards keep the app's compact "€20,10" style). */
  static formatMoneyInSentence(cents, currency) {
    const sym = currency || '€';
    const lang = (document.documentElement.lang || '').toLowerCase();
    if (lang.startsWith('en')) return sym + (cents / 100).toFixed(2);
    return (cents / 100).toFixed(2).replace('.', ',') + ' ' + sym;
  }

  static formatGrams(g) {
    const ru = (document.documentElement.lang || '').toLowerCase().startsWith('ru');
    if (g >= 1000) {
      return (g / 1000).toFixed(g % 1000 === 0 ? 0 : 1).replace('.', ',') + (ru ? 'кг' : 'kg');
    }
    return g + (ru ? 'г' : 'g');
  }

  static boot() {
    const offerBar = document.querySelector('.dl-offer-bar');
    if (!offerBar) return;
    const dataEl = document.getElementById('dl-offer-data');
    if (!dataEl) return;
    new DlOfferBar(offerBar, dataEl).init();
    // The .pdp-price-compact price sync (offer tiers + quantity) lives in component-product-form.js.
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => DlOfferBar.boot());
} else {
  DlOfferBar.boot();
}
